import React from 'react';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { CashDenominationTable } from './CashDenominationTable';
import { AmountCaptureList } from './AmountCaptureList';

const BRANCHES = [
  { id: 'malbon-hq', label: 'Malbon HQ - District 1' },
  { id: 'malbon-d2', label: 'Malbon Riverside - District 2' },
  { id: 'malbon-thuduc', label: 'Malbon Thu Duc' },
];

const createCashState = () => ({
  500000: 0,
  200000: 0,
  100000: 0,
  50000: 0,
  20000: 0,
  10000: 0,
  5000: 0,
});

export function ShiftClosingCard() {
  const [branchId, setBranchId] = React.useState(BRANCHES[0].id);
  const [cashEntries, setCashEntries] = React.useState(() => createCashState());
  const [cardEntries, setCardEntries] = React.useState([]);
  const [shippingEntries, setShippingEntries] = React.useState([]);
  const [otpCode, setOtpCode] = React.useState('');
  const [isSummaryOpen, setSummaryOpen] = React.useState(false);

  const totalCash = React.useMemo(
    () => Object.entries(cashEntries).reduce((acc, [denom, qty]) => acc + Number(denom) * qty, 0),
    [cashEntries],
  );
  const totalCard = React.useMemo(
    () => cardEntries.reduce((acc, amount) => acc + amount, 0),
    [cardEntries],
  );
  const totalShipping = React.useMemo(
    () => shippingEntries.reduce((acc, amount) => acc + amount, 0),
    [shippingEntries],
  );

  const handleCashChange = (denomination, qty) => {
    setCashEntries((prev) => ({
      ...prev,
      [denomination]: Math.max(0, qty),
    }));
  };

  const openSummary = () => {
    setSummaryOpen(true);
  };

  const closeSummary = () => {
    setSummaryOpen(false);
  };

  const resetForm = () => {
    setCashEntries(createCashState());
    setCardEntries([]);
    setShippingEntries([]);
    setOtpCode('');
  };

  const confirmSubmission = () => {
    closeSummary();
    resetForm();
  };

  return (
    <>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <div>
              <Typography variant="h5" fontWeight={700}>
                Shift closing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Capture every payment channel before handing over the shift.
              </Typography>
            </div>
            <TextField
              select
              size="small"
              label="Select branch"
              value={branchId}
              onChange={(event) => setBranchId(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 260 } }}
            >
              {BRANCHES.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              Cash on hand
            </Typography>
            <CashDenominationTable values={cashEntries} onChange={handleCashChange} />
            <Typography variant="body1" fontWeight={600} textAlign="right">
              Total cash: {totalCash.toLocaleString('en-US')} ₫
            </Typography>
          </Stack>

          <Divider />

          <AmountCaptureList
            label="Card transactions"
            placeholder="Enter card amount"
            entries={cardEntries}
            onAdd={(value) => setCardEntries((prev) => [...prev, value])}
            onRemove={(index) =>
              setCardEntries((prev) => prev.filter((_, entryIndex) => entryIndex !== index))
            }
          />

          <Divider />

          <AmountCaptureList
            label="Shipping orders"
            placeholder="Enter shipping amount"
            entries={shippingEntries}
            onAdd={(value) => setShippingEntries((prev) => [...prev, value])}
            onRemove={(index) =>
              setShippingEntries((prev) => prev.filter((_, entryIndex) => entryIndex !== index))
            }
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
            <Button variant="outlined" color="inherit" onClick={resetForm}>
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={openSummary}
              disabled={totalCash === 0 && totalCard === 0 && totalShipping === 0}
            >
              Review & confirm
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Dialog open={isSummaryOpen} onClose={closeSummary} fullWidth maxWidth="sm">
        <DialogTitle>Shift summary</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Branch: {BRANCHES.find((branch) => branch.id === branchId)?.label}
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              Cash: {totalCash.toLocaleString('en-US')} ₫
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              Card transactions: {totalCard.toLocaleString('en-US')} ₫
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              Shipping orders: {totalShipping.toLocaleString('en-US')} ₫
            </Typography>
            <TextField
              label="OTP"
              placeholder="Enter 6-digit OTP"
              value={otpCode}
              onChange={(event) => setOtpCode(event.target.value)}
              inputProps={{ maxLength: 6 }}
              sx={{ mt: 2 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSummary}>Cancel</Button>
          <Button variant="contained" disabled={!otpCode} onClick={confirmSubmission}>
            Submit shift
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


